// src/components/contacts/ContactCompaniesSection.tsx
//
// GATE B5 — "Affiliated Companies" on the Contact detail slide-over.
//
// A contact may belong to several companies with a DIFFERENT role at each, and
// exactly one of those affiliations may be marked primary. This section is the
// authoritative place that relationship is managed from the contact side.
//
// Follows `ContactCirclesSection`, the existing "this contact belongs to these
// things, with add/remove actions" panel on the same screen — same layout, same
// inline banner, same click-through. No new design vocabulary.
//
// GATING (capability, never role):
//   render list      contacts.read   — a viewer SEES affiliations
//   any mutation     contacts.write  — link / edit role / set primary / unlink
// These routes are contact-side, so they gate on contacts.*. The company-side
// equivalents live on Company detail and gate on companies.*.
//   create company   companies.write — inline in the link dialog, through
//                    `POST /api/companies`, never a contacts-scoped route
//
// PRIMARY REMOVAL: the backend refuses to guess a replacement. When the primary
// is removed while others remain the user chooses: another company becomes
// primary, or — explicitly — none does.
//
// RELATIONSHIP DETAILS: each row shows the role title at the company, the
// relationship type, start/end dates and a Current/Former badge (derived by
// the server from the end date). Former relationships are listed under their
// own "Former" heading. Ending the PRIMARY relationship asks for a successor
// the same way removal does — a primary must always be current.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetContactCompaniesQuery,
  useLinkCompanyToContactMutation,
  useUpdateAffiliationMutation,
  useRemoveAffiliationMutation,
  affiliationErrorMessage,
  RELATIONSHIP_TYPE_OPTIONS,
  type AffiliationRelationshipType,
  type ContactCompanyAffiliation,
} from '../../api/contactCompanyApi';
import {
  EditRelationshipDialog,
  NO_PRIMARY,
  RelationshipBadges,
  relationshipSummary,
  type RelationshipEdit,
} from './AffiliationRelationship';
import { useGetCompaniesQuery } from '../../api/companiesApi';
import {
  useCompanyPermissions,
  useContactPermissions,
} from '../../hooks/useContactCompanyPermissions';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  normalizeCompanyName,
  useCreateCompanyResolvingDuplicate,
  type CompanyChoice,
} from './CompanyMultiPicker';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../ui/Modal';

interface Props {
  contactId: string;
  contactName: string;
}

type Banner = { kind: 'success' | 'error'; message: string } | null;

export { NO_PRIMARY };

export default function ContactCompaniesSection({ contactId, contactName }: Props) {
  const { canViewContacts, canEditContact } = useContactPermissions();

  const {
    data: affiliations = [],
    isLoading,
    error,
  } = useGetContactCompaniesQuery(contactId, { skip: !canViewContacts });

  const [linkCompany, { isLoading: isLinking }] = useLinkCompanyToContactMutation();
  const [updateAffiliation] = useUpdateAffiliationMutation();
  const [removeAffiliation] = useRemoveAffiliationMutation();

  const [banner, setBanner] = useState<Banner>(null);
  const [showLink, setShowLink] = useState(false);
  const [editing, setEditing] = useState<ContactCompanyAffiliation | null>(null);
  const [removing, setRemoving] = useState<ContactCompanyAffiliation | null>(null);
  const [replacementId, setReplacementId] = useState<string>('');

  // Without contacts.read there is nothing to show. The section disappears
  // rather than rendering an empty shell that implies missing data.
  if (!canViewContacts) return null;

  const primary = affiliations.find((a) => a.isPrimary) ?? null;
  // `isCurrent !== false` so a row from an older server (no field) reads as current.
  const current = affiliations.filter((a) => a.isCurrent !== false);
  const former = affiliations.filter((a) => a.isCurrent === false);

  async function handleSetPrimary(aff: ContactCompanyAffiliation) {
    try {
      await updateAffiliation({
        side: 'contact',
        ownerId: contactId,
        affiliationId: aff.affiliationId,
        counterpartId: aff.companyId,
        isPrimary: true,
      }).unwrap();
      setBanner({ kind: 'success', message: `${aff.company?.name ?? 'Company'} is now the primary company.` });
    } catch (err) {
      setBanner({ kind: 'error', message: affiliationErrorMessage(err) });
    }
  }

  /** Rejects with the API error so the dialog can show it in place. */
  async function handleSaveRelationship(aff: ContactCompanyAffiliation, edit: RelationshipEdit) {
    await updateAffiliation({
      side: 'contact',
      ownerId: contactId,
      affiliationId: aff.affiliationId,
      counterpartId: aff.companyId,
      ...edit,
    }).unwrap();
    setEditing(null);
    setBanner({ kind: 'success', message: 'Relationship updated.' });
  }

  async function handleRemove(aff: ContactCompanyAffiliation, replacement?: string) {
    try {
      await removeAffiliation({
        side: 'contact',
        ownerId: contactId,
        affiliationId: aff.affiliationId,
        counterpartId: aff.companyId,
        ...(replacement === NO_PRIMARY
          ? { allowNoPrimary: true }
          : { replacementPrimaryId: replacement || undefined }),
      }).unwrap();
      setRemoving(null);
      setReplacementId('');
      setBanner({ kind: 'success', message: `Unlinked from ${aff.company?.name ?? 'company'}.` });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'PRIMARY_REPLACEMENT_REQUIRED') {
        // Expected, not exceptional: the backend is asking the user to decide.
        // Keep the dialog open and surface the choice instead of failing.
        setRemoving(aff);
        setBanner({ kind: 'error', message: affiliationErrorMessage(err) });
        return;
      }
      setRemoving(null);
      setBanner({ kind: 'error', message: affiliationErrorMessage(err) });
    }
  }

  function renderRow(aff: ContactCompanyAffiliation) {
    const summary = relationshipSummary(aff);
    const isCurrent = aff.isCurrent !== false;
    return (
      <li
        key={aff.affiliationId}
        className={`flex flex-col gap-2 rounded border px-3 py-2 sm:flex-row sm:items-start sm:justify-between ${
          isCurrent ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={`/contacts/companies/${aff.companyId}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 break-words"
            >
              {aff.company?.name ?? 'Company'}
            </Link>
            <RelationshipBadges isPrimary={aff.isPrimary} isCurrent={isCurrent} />
          </div>
          <p className="text-xs text-gray-600 mt-0.5 break-words">{aff.roleTitle || 'No role set'}</p>
          {summary && <p className="text-xs text-gray-500 break-words">{summary}</p>}
        </div>

        {canEditContact && (
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setEditing(aff)}
              aria-label={`Edit relationship with ${aff.company?.name ?? 'company'}`}
              className="text-xs text-gray-600 hover:text-gray-800"
            >
              Edit details
            </button>
            {!aff.isPrimary && isCurrent && (
              <button
                type="button"
                onClick={() => handleSetPrimary(aff)}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Make primary
              </button>
            )}
            <button
              type="button"
              onClick={() => setRemoving(aff)}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Unlink
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Companies</h3>
        {canEditContact && (
          <button
            type="button"
            onClick={() => setShowLink(true)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Link company
          </button>
        )}
      </div>

      {banner && (
        <div
          role="status"
          className={`mb-3 rounded px-3 py-2 text-sm ${
            banner.kind === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {banner.message}
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <p className="text-sm text-gray-500">Company links are unavailable right now.</p>
      ) : affiliations.length === 0 ? (
        <p className="text-sm text-gray-500">
          {contactName} is not linked to any companies yet.
        </p>
      ) : (
        <>
          {current.length > 0 && (
            <ul className="space-y-2" aria-label="Current companies">
              {current.map((aff) => renderRow(aff))}
            </ul>
          )}
          {former.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Former</h4>
              <ul className="space-y-2" aria-label="Former companies">
                {former.map((aff) => renderRow(aff))}
              </ul>
            </div>
          )}
        </>
      )}

      {showLink && (
        <LinkCompanyModal
          contactId={contactId}
          existingCompanyIds={affiliations.map((a) => a.companyId)}
          hasPrimary={primary !== null}
          isSubmitting={isLinking}
          onClose={() => setShowLink(false)}
          onSubmit={async (companyId, details, makePrimary) => {
            try {
              await linkCompany({ contactId, companyId, ...details, isPrimary: makePrimary }).unwrap();
              setShowLink(false);
              setBanner({ kind: 'success', message: 'Company linked.' });
            } catch (err) {
              setBanner({ kind: 'error', message: affiliationErrorMessage(err) });
            }
          }}
        />
      )}

      {editing && (
        <EditRelationshipDialog
          title={`Relationship with ${editing.company?.name ?? 'company'}`}
          affiliation={editing}
          replacementCandidates={affiliations
            .filter((a) => a.affiliationId !== editing.affiliationId && a.isCurrent !== false)
            .map((a) => ({ affiliationId: a.affiliationId, name: a.company?.name ?? 'Company' }))}
          onSave={(edit) => handleSaveRelationship(editing, edit)}
          onClose={() => setEditing(null)}
        />
      )}

      {removing && (
        <UnlinkModal
          affiliation={removing}
          // Only a CURRENT relationship may become primary.
          others={affiliations.filter(
            (a) => a.affiliationId !== removing.affiliationId && a.isCurrent !== false,
          )}
          replacementId={replacementId}
          onReplacementChange={setReplacementId}
          onCancel={() => {
            setRemoving(null);
            setReplacementId('');
          }}
          onConfirm={() => handleRemove(removing, replacementId)}
        />
      )}
    </div>
  );
}

// ── Link modal ─────────────────────────────────────────────────────────────
//
// Selects an EXISTING company, or creates one inline when nothing in the
// workspace has that name. Creation goes through the same duplicate-safe hook
// the contact form's company picker uses, so both agree on what "already
// exists" means and a lost race links the winner instead of erroring.

function LinkCompanyModal({
  contactId,
  existingCompanyIds,
  hasPrimary,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  contactId: string;
  existingCompanyIds: string[];
  hasPrimary: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (
    companyId: string,
    details: {
      roleTitle: string | null;
      relationshipType?: AffiliationRelationshipType;
      startDate?: string;
    },
    makePrimary: boolean,
  ) => void;
}) {
  const { canCreateCompany } = useCompanyPermissions();
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search.trim(), 250);
  const [selected, setSelected] = useState<string>('');
  const [created, setCreated] = useState<CompanyChoice | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [roleTitle, setRoleTitle] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [makePrimary, setMakePrimary] = useState(false);
  const { create, isCreating } = useCreateCompanyResolvingDuplicate();

  const { data, isLoading, isFetching } = useGetCompaniesQuery({ q: debounced || undefined, limit: 50 });

  const companies = useMemo(() => {
    const list = (data as { companies?: Array<{ id: string; name: string; domain?: string | null }> })?.companies ?? [];
    // Already-linked companies are filtered out so the UI cannot invite a
    // duplicate the backend would reject.
    return list.filter((c) => !existingCompanyIds.includes(c.id));
  }, [data, existingCompanyIds]);

  const typed = search.trim();
  const settled = debounced === typed && !isFetching;
  const nameTaken = ((data as { companies?: Array<{ name: string }> })?.companies ?? []).some(
    (c) => normalizeCompanyName(c.name) === normalizeCompanyName(typed),
  );
  const offerCreate = canCreateCompany && typed.length > 0 && settled && !nameTaken && !created;

  async function handleCreate() {
    setCreateError(null);
    try {
      const company = await create(typed);
      if (existingCompanyIds.includes(company.id)) {
        setCreateError(`${company.name} is already linked to this contact.`);
        return;
      }
      setCreated(company);
      setSelected(company.id);
    } catch (err) {
      setCreateError((err as Error).message);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Link a company" size="md">
      <div className="space-y-3">
        <input
          aria-label="Search companies"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies…"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
        />

        {/*
          The shared Modal constrains WIDTH only — it has no max-height or
          scroll region (a known, still-open defect). Capping this list keeps
          the dialog inside the viewport regardless, so a long company list
          scrolls internally instead of pushing the actions off-screen.
        */}
        <div className="max-h-56 overflow-y-auto border border-gray-200 rounded divide-y">
          {created && (
            <button
              type="button"
              onClick={() => setSelected(created.id)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                selected === created.id ? 'bg-blue-50 text-blue-800' : ''
              }`}
              aria-pressed={selected === created.id}
            >
              {created.name} <span className="text-xs text-gray-500">(new)</span>
            </button>
          )}
          {isLoading ? (
            <div className="p-3"><LoadingSpinner /></div>
          ) : companies.length === 0 && !created ? (
            <p className="p-3 text-sm text-gray-500">No matching companies.</p>
          ) : (
            companies
              .filter((c) => c.id !== created?.id)
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelected(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    selected === c.id ? 'bg-blue-50 text-blue-800' : ''
                  }`}
                  aria-pressed={selected === c.id}
                >
                  {c.name}
                  {c.domain && <span className="ml-2 text-xs text-gray-500">{c.domain}</span>}
                </button>
              ))
          )}
          {offerCreate && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={isCreating}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
            >
              {isCreating ? 'Creating…' : `+ Create company “${typed}”`}
            </button>
          )}
        </div>
        {createError && (
          <p role="alert" className="text-sm text-red-600">{createError}</p>
        )}

        <input
          aria-label="Role at this company"
          value={roleTitle}
          onChange={(e) => setRoleTitle(e.target.value)}
          placeholder="Role at this company (optional)"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
        />

        {/* Optional and collapsed: linking stays one step for anyone who
            does not need the detail. */}
        <details className="rounded border border-gray-200">
          <summary className="cursor-pointer select-none px-3 py-2 text-sm text-gray-700">
            Relationship details <span className="text-gray-500">(optional)</span>
          </summary>
          <div className="grid grid-cols-1 gap-2 px-3 pb-3 min-[360px]:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="link-company-type" className="block text-xs text-gray-600">
                Relationship type
              </label>
              <select
                id="link-company-type"
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                className="w-full min-w-0 border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="">Not specified</option>
                {RELATIONSHIP_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label htmlFor="link-company-start" className="block text-xs text-gray-600">
                Start date
              </label>
              <input
                id="link-company-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full min-w-0 border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
        </details>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={makePrimary}
            onChange={(e) => setMakePrimary(e.target.checked)}
          />
          Make this the primary company
          {!hasPrimary && <span className="text-xs text-gray-500">(no primary set)</span>}
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600">
            Cancel
          </button>
          <button
            type="button"
            disabled={!selected || isSubmitting}
            onClick={() =>
              onSubmit(
                selected,
                {
                  roleTitle: roleTitle.trim() || null,
                  ...(relationshipType
                    ? { relationshipType: relationshipType as AffiliationRelationshipType }
                    : {}),
                  ...(startDate ? { startDate } : {}),
                },
                makePrimary,
              )
            }
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {isSubmitting ? 'Linking…' : 'Link company'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Unlink modal ───────────────────────────────────────────────────────────
//
// Removing a NON-primary affiliation is a plain confirm. Removing the PRIMARY
// while others remain requires an explicit choice — another company becomes
// primary, or none does. The service refuses to promote one on the user's
// behalf, so the UI must ask; "no primary" is offered as a real choice rather
// than a dead end.

function UnlinkModal({
  affiliation,
  others,
  replacementId,
  onReplacementChange,
  onCancel,
  onConfirm,
}: {
  affiliation: ContactCompanyAffiliation;
  others: ContactCompanyAffiliation[];
  replacementId: string;
  onReplacementChange: (id: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const needsReplacement = affiliation.isPrimary && others.length > 0;
  const name = affiliation.company?.name ?? 'this company';

  return (
    <Modal isOpen onClose={onCancel} title={`Unlink ${name}?`} size="sm">
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          This removes the link only. The contact and the company both remain.
        </p>

        {needsReplacement && (
          <div className="space-y-1.5">
            <p className="text-sm text-gray-700">
              {name} is the primary company. Choose which company becomes primary, or none:
            </p>
            <select
              aria-label="Replacement primary company"
              value={replacementId}
              onChange={(e) => onReplacementChange(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select a company…</option>
              {others.map((o) => (
                <option key={o.affiliationId} value={o.affiliationId}>
                  {o.company?.name ?? 'Company'}
                </option>
              ))}
              <option value={NO_PRIMARY}>No primary company</option>
            </select>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600">
            Cancel
          </button>
          <button
            type="button"
            disabled={needsReplacement && !replacementId}
            onClick={onConfirm}
            className="px-3 py-2 text-sm bg-red-600 text-white rounded disabled:opacity-50"
          >
            Unlink
          </button>
        </div>
      </div>
    </Modal>
  );
}
